import React, { useState, useEffect } from 'react';
import useStores from '../../hooks/useStores';
import Image from '../Image';
import { Sizes } from '../../constants/Image';
import LocalizedLink from '../LocalizedLink';
import Price from '../Price/Price';
import QuantityButton from './QuantityButton';
import Translate from '../Translate/Translate';
import './styles/CheckoutProducts.scss';
import { observer } from 'mobx-react';
import DiscountCode from './DiscountCode';
import OpenModalFromMail from '../Wishlist/OpenModalFromMail';
import { useSearchParams } from 'react-router-dom';
import Modal from '../Modal';
import SendToMail from '../Wishlist/SendToMail';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import DiscountPercentage from '../Price/DiscountPercentage';

const CheckoutProducts = () => {
  const { cart, authentication, user } = useStores();
  const [cartItems, setCartItems] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const uuid = searchParams.get('uuid');

  useEffect(() => {
    setCartItems(cart.variations);
  }, [cart.variations]);

  const removeProduct = async (variation, quantity) => {
    await cart.removeItem(variation.code, quantity);
    setCartItems(cart.variations);
  };

  const hasShopRole = user.roles.includes('shop');

  const isAuthenticated = authentication?.loggedIn || false;

  return (
    <div>
      <div className="hidden font-axi-bold lg:flex lg:pb-5">
        <div className="flex-[0_1_450px]">
          <p>
            <Translate component="checkout" keyword="th-products" />
          </p>
        </div>
        <div className="flex-[0_0_193px] text-center">
          <p>
            <Translate component="checkout" keyword="th-amount" />
          </p>
        </div>
        <div className="flex-[0_0_193px] text-center">
          <p>
            <Translate component="checkout" keyword="th-price" />
          </p>
        </div>
        <div className="flex-[0_0_193px] text-center">
          <p>
            <Translate component="checkout" keyword="th-sum" />
          </p>
        </div>
      </div>
      <div className="flex flex-wrap lg:flex-nowrap lg:flex-col mb-5 -mx-2.5 lg:mx-0 lg:pt-9 lg:border-rgrey-light lg:border-b lg:border-solid lg:border-t">
        {cartItems.length ? (
          cartItems.map((variation, index) => {
            const product = cart.getProduct(variation.product_id);

            if (product) {
              return (
                <div
                  key={index}
                  className="flex pt-9 px-2.5 lg:px-0 lg:pt-0 lg:pb-5"
                >
                  <div className="relative mr-4">
                    <LocalizedLink
                      className="mr-4"
                      to={`/toode/${product.slug}`}
                    >
                      <Image
                        className={
                          'rounded-sm bg-rgrey-lightlight object-cover max-w-32 md:max-w-52 min-w-32 h-auto w-auto'
                        }
                        defaultSize={Sizes.W800}
                        image={product.cover}
                        allSizes={[Sizes.W300, Sizes.W540, Sizes.W800]}
                      />
                    </LocalizedLink>
                    <DiscountPercentage
                      prices={product.prices(isAuthenticated)}
                      productNew={product.isNew}
                      componentClassName="absolute left-0 text-xs!"
                      containerClassName="pt-2 pl-2 md:pt-4 md:pl-3"
                      hidePercentage={!isAuthenticated}
                    />
                  </div>
                  <div className="component-checkoutProducts-content">
                    <div className="component-checkoutProducts-titles">
                      <p className="text-xs font-axi-bold text-rgrey lg:text-sm">
                        {product.brandTitle} {product.gridTitle}
                      </p>
                      <p className="font-axi-bold text-lg lg:text-xl">
                        {product.title}
                      </p>
                    </div>
                    <div className="component-checkoutProducts-sizes">
                      <span className="component-checkoutProducts-size">
                        {variation.value}
                      </span>
                      <div className="lg:hidden">
                        <QuantityButton data={variation} />
                      </div>
                    </div>
                    <Price
                      prices={product.prices(isAuthenticated)}
                      priceLabel={true}
                      productNew={product.isNew}
                      discountPriceClassname="text-base mr-1"
                      defaultPriceClassName="text-xs text-nowrap"
                      discountLabelClassname="text-xs"
                    />
                    {product.product_code !== '4370' &&
                    product.product_code !== '4340' ? (
                      <div className="component-checkoutProducts-remove__desktop">
                        <p
                          onClick={() =>
                            removeProduct(variation, variation.quantity)
                          }
                        >
                          <Translate
                            component="checkout"
                            keyword="remove-product"
                          />
                        </p>
                      </div>
                    ) : null}
                  </div>
                  <div className="component-checkoutProducts-desktop">
                    {product.product_code !== '4370' &&
                    product.product_code !== '4340' ? (
                      <QuantityButton data={variation} />
                    ) : (
                      <div />
                    )}
                    <Price
                      prices={product.prices(isAuthenticated)}
                      priceLabel={true}
                      productNew={product.isNew}
                      discountPriceClassname="text-base mr-1"
                      defaultPriceClassName="text-sm"
                      discountLabelClassname="text-xs"
                    />
                    <p className="component-checkoutProducts-desktop__sum">
                      {(
                        variation.quantity *
                        product.finalPrice(authentication.loggedIn)
                      ).toFixed(2)}{' '}
                      €
                    </p>
                  </div>
                  {product.product_code !== '4370' &&
                  product.product_code !== '4340' ? (
                    <div
                      className="component-checkoutProducts-remove"
                      onClick={() =>
                        removeProduct(variation, variation.quantity)
                      }
                    ></div>
                  ) : null}
                </div>
              );
            }
          })
        ) : (
          <div className="flex justify-center flex-1 pt-15 lg:pt-9 pb-5 lg:pb-16 px-0">
            <h3 className="text-3xl leading-10 font-axi-bold">
              <Translate component="mini-cart" keyword="empty" />
            </h3>
          </div>
        )}
      </div>
      {hasShopRole && cartItems.length ? (
        <div className="md:my-2 lg:pb-5">
          <Modal
            buttonClass="font-axi-bold py-3 text-black hover:underline cursor-pointer flex items-center"
            button="Saada ostukorv kliendile"
            content={
              <SendToMail
                onClose={() => {
                  cart.load();
                }}
              />
            }
            contentTitle={'Kliendi andmed'}
            modalClass="center overflow-auto min-h-fit"
            icon={<EnvelopeIcon className="h-5 w-5 mr-2" />}
          />
        </div>
      ) : null}

      <div className="hidden lg:flex">
        <DiscountCode />
        <div className="flex items-center max-h-12 ml-auto">
          <p>
            <Translate component="checkout" keyword="right-total" />
            <span className="ml-1 mr-6 font-axi-bold text-base">
              {cart.totalSum} €
            </span>
          </p>
        </div>
      </div>
      <OpenModalFromMail
        uuid={uuid}
        onClose={() => {
          setSearchParams({});
          cart.load();
        }}
      />
    </div>
  );
};

export default observer(CheckoutProducts);
